import {
  ADMIN_BUSINESS_ROLE_IDS,
  ADMIN_DASHBOARD_ROLE_ID,
} from '@/constants/adminRoles';
import {
  EXPORT_PERMISSION_MAP,
  FUNCTION_PERMISSION_MAP,
  ROUTE_PERMISSION_MAP,
} from '../constants';
import type {
  ExportPermissionNode,
  MenuItemWithPermission,
  PermissionNode,
  PermissionType,
  UserPermissions,
} from '../types';

/**
 * 核心权限服务类
 * 统一处理所有权限相关的逻辑
 */
export class PermissionService {
  private systemPermissions: number[] = [];
  private exportPermissions: number[] = [];
  private permissionTree: PermissionNode[] = [];
  private roleIds: number[] = [];
  private systemAdmin = false;
  private currentUsername = '';

  /**
   * 初始化用户权限
   */
  initialize(userPermissions: UserPermissions) {
    this.systemPermissions = userPermissions.systemPermissions || [];
    this.exportPermissions = userPermissions.exportPermissions || [];
  }

  /**
   * 设置权限树
   */
  setPermissionTrees(
    systemTree: PermissionNode[],
    _exportTree: ExportPermissionNode[],
  ) {
    this.permissionTree = systemTree;
  }

  /**
   * 设置用户角色ID列表
   */
  setRoleIds(roleIds: number[]) {
    this.roleIds = Array.isArray(roleIds) ? roleIds : [];
  }

  setSystemAdmin(isSystemAdmin: boolean) {
    this.systemAdmin = Boolean(isSystemAdmin);
  }

  /**
   * 设置当前用户名，用于 System Administrator 专属路由判断
   */
  setCurrentUsername(username?: string) {
    this.currentUsername = username || '';
  }

  /**
   * 检查系统权限
   */
  hasSystemPermission(permissionId: number): boolean {
    return this.systemPermissions.includes(permissionId);
  }

  /**
   * 检查导出权限
   */
  hasExportPermission(permissionId: number): boolean {
    return this.exportPermissions.includes(permissionId);
  }

  /**
   * 检查功能权限（通过权限名称）
   */
  hasFunctionPermission(functionName: string): boolean {
    const permissionId = FUNCTION_PERMISSION_MAP[functionName];
    if (permissionId) {
      return this.hasSystemPermission(permissionId);
    }
    // 如果在映射中找不到，尝试在权限树中搜索
    return this.findPermissionInTree(this.permissionTree, functionName);
  }

  /**
   * 检查路由权限
   */
  hasRoutePermission(routePath: string): boolean {
    const normalizedPath = this.normalizeRoutePath(routePath);

    if (
      normalizedPath === '/' ||
      normalizedPath === '/403' ||
      normalizedPath.startsWith('/user/')
    ) {
      return true;
    }

    const isSystemAdmin = this.systemAdmin || this.isSystemAdministrator();

    if (normalizedPath === '/system' || normalizedPath.startsWith('/system/')) {
      return isSystemAdmin;
    }

    if (isSystemAdmin) {
      return normalizedPath === '/dashboard';
    }

    const permissionId = ROUTE_PERMISSION_MAP[normalizedPath];
    if (!permissionId) {
      return false;
    }

    if (normalizedPath === '/dashboard') {
      return this.canAccessDashboard();
    }

    return this.canAccessBusinessRoutes();
  }

  private canAccessDashboard(): boolean {
    return this.roleIds.includes(ADMIN_DASHBOARD_ROLE_ID);
  }

  private canAccessBusinessRoutes(): boolean {
    return (
      this.canAccessDashboard() ||
      this.roleIds.some((roleId) => ADMIN_BUSINESS_ROLE_IDS.includes(roleId))
    );
  }

  private isSystemAdministrator(): boolean {
    return this.currentUsername === 'wwadmin';
  }

  private normalizeRoutePath(routePath: string): string {
    if (!routePath) {
      return '/';
    }
    const normalized = routePath.replace(/\/+$/, '');
    return normalized || '/';
  }

  /**
   * 统一的权限检查方法
   */
  checkPermission(
    permission: string | number,
    type: PermissionType = 'system',
  ): boolean {
    switch (type) {
      case 'system':
        return typeof permission === 'number'
          ? this.hasSystemPermission(permission)
          : this.hasFunctionPermission(permission);
      case 'function':
        return typeof permission === 'string'
          ? this.hasFunctionPermission(permission)
          : false;
      case 'export':
        if (typeof permission === 'number') {
          return this.hasExportPermission(permission);
        }
        if (typeof permission === 'string') {
          const permissionId = EXPORT_PERMISSION_MAP[permission];
          return permissionId ? this.hasExportPermission(permissionId) : false;
        }
        return false;
      default:
        return false;
    }
  }

  /**
   * 检查是否有任意一个权限
   */
  hasAnyPermission(permissionIds: number[]): boolean {
    return permissionIds.some((id) => this.hasSystemPermission(id));
  }

  /**
   * 检查是否有所有权限
   */
  hasAllPermissions(permissionIds: number[]): boolean {
    return permissionIds.every((id) => this.hasSystemPermission(id));
  }

  /**
   * 过滤可访问的菜单
   */
  filterAccessibleMenus(
    menus: MenuItemWithPermission[],
  ): MenuItemWithPermission[] {
    return menus
      .map((menu) => this.processMenuItem(menu))
      .filter((menu) => menu !== null) as MenuItemWithPermission[];
  }

  /**
   * 处理单个菜单项
   */
  private processMenuItem(
    menu: MenuItemWithPermission,
  ): MenuItemWithPermission | null {
    // 检查当前菜单项的路由权限
    const hasRoutePermission = menu.path
      ? this.hasRoutePermission(menu.path)
      : true;

    // 如果有子菜单，递归处理
    if (menu.routes && menu.routes.length > 0) {
      const accessibleChildren = this.filterAccessibleMenus(menu.routes);

      // 如果有可访问的子菜单，保留父菜单
      if (accessibleChildren.length > 0) {
        return {
          ...menu,
          routes: accessibleChildren,
        };
      }

      // 如果没有可访问的子菜单，检查父菜单本身是否有权限
      return hasRoutePermission ? { ...menu, routes: [] } : null;
    }

    // 叶子菜单项，直接检查权限
    return hasRoutePermission ? menu : null;
  }

  /**
   * 在权限树中递归查找权限
   */
  private findPermissionInTree(nodes: PermissionNode[], name: string): boolean {
    for (const node of nodes) {
      if (node.name === name && this.systemPermissions.includes(node.id)) {
        return true;
      }
      if (node.children && this.findPermissionInTree(node.children, name)) {
        return true;
      }
    }
    return false;
  }

  /**
   * 获取用户所有权限ID
   */
  getAllPermissions(): number[] {
    return [...this.systemPermissions, ...this.exportPermissions];
  }

  /**
   * 获取用户可访问的路径列表
   */
  getAccessiblePaths(): string[] {
    return Object.keys(ROUTE_PERMISSION_MAP).filter((path) =>
      this.hasRoutePermission(path),
    );
  }

  /**
   * 获取权限名称（用于调试和日志）
   */
  getPermissionName(permissionId: number): string {
    // 从功能权限映射中查找
    for (const [name, id] of Object.entries(FUNCTION_PERMISSION_MAP)) {
      if (id === permissionId) return name;
    }

    // 从权限树中查找
    const found = this.findPermissionNameInTree(
      this.permissionTree,
      permissionId,
    );
    if (found) return found;

    return `权限${permissionId}`;
  }

  /**
   * 在权限树中查找权限名称
   */
  private findPermissionNameInTree(
    nodes: PermissionNode[],
    permissionId: number,
  ): string | null {
    for (const node of nodes) {
      if (node.id === permissionId) {
        return node.name;
      }
      if (node.children) {
        const found = this.findPermissionNameInTree(
          node.children,
          permissionId,
        );
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * 重置权限数据
   */
  reset() {
    this.systemPermissions = [];
    this.exportPermissions = [];
    this.permissionTree = [];
    this.roleIds = [];
    this.systemAdmin = false;
    this.currentUsername = '';
  }

  /**
   * 获取当前权限状态（用于调试）
   */
  getPermissionStatus() {
    return {
      systemPermissions: this.systemPermissions,
      exportPermissions: this.exportPermissions,
      currentUsername: this.currentUsername,
      totalPermissions: this.getAllPermissions().length,
    };
  }
}

// 创建全局权限服务实例
export const permissionService = new PermissionService();
